<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'

import { useConfigStore } from '../../../stores/config'
import { cleanUpSearchPhrase } from '../../../utils/strings'
import EpgAutocompleteFlyout from './EpgSearch/EpgAutocompleteFlyout.vue'
import Icon from '../../../components/shared/Icon.vue'
import { useKeepTabbingFocus } from '../../../composables/use-keep-tabbing-focus'
import { getAxios } from '../../../api/axios'

import type { EpgAutocompleteSearchResult } from '@xcel-next/composer-schema'

type SearchType = 'epg' | 'wer-streamt' | 'suche'

type SearchTypeOption = {
  value: SearchType,
  label: string,
  placeholder: string
}

const searchTypes: SearchTypeOption[] = [
  {
    value: 'epg',
    label: 'TV Programm',
    placeholder: 'Sender und Sendungen suchen'
  },
  {
    value: 'wer-streamt',
    label: 'Streaming',
    placeholder: 'Nach Streaming-Titeln suchen'
  },
  {
    value: 'suche',
    label: 'News',
    placeholder: 'Nach TV-News suchen'
  }
]

const createEmptyEpgAutocompleteSearch = (): EpgAutocompleteSearchResult => ({ channels: [], broadcasts: {} })

const { keepTabbingFocus, removeTabbingEventListeners } = useKeepTabbingFocus()
const { rsConfig } = storeToRefs(useConfigStore())

const headerSearchOpener = ref<HTMLButtonElement | null>(null)
const headerSearchForm = ref<HTMLFormElement | null>(null)
const searchInput = ref<HTMLInputElement | null>(null)

const hasApiError = ref(false)
const searchInProgress = ref(false)
const searchPhrase = ref('')
const showSearch = ref(false)
const searchType = ref<SearchType>('epg') // ToDo: Prevent fallback to default after submiting search
const epgAutocompleteSearch = ref<EpgAutocompleteSearchResult>(createEmptyEpgAutocompleteSearch())
let debounceTimeoutId: ReturnType<typeof setTimeout> | undefined

const placeholder = computed(() => searchTypes.find(type => type.value === searchType.value)?.placeholder)
const isEpgSearch = computed(() => searchType.value === 'epg')

const setFocus = () => {
  searchInput.value?.focus()
}

const resetEpgAutocompleteSearchResults = () => {
  epgAutocompleteSearch.value = createEmptyEpgAutocompleteSearch()
}

const keepTabbingFocusInHeaderSearch = () => {
  nextTick(() => {
    removeTabbingEventListeners()
    if (!headerSearchForm.value) return
    keepTabbingFocus(headerSearchForm.value, ['input, button:not(.bx-header__search-close):not(.bx-form__search-submit), .bx-epg-search--autocomplete a'], 0, () => {
      removeTabbingEventListeners()
      toggleSearch()
      headerSearchOpener.value?.focus()
    })
  })
}

const toggleSearch = () => {
  showSearch.value = document.body.classList.toggle('bx-js-show-search')
  if (!showSearch.value) return
  setFocus()
  keepTabbingFocusInHeaderSearch()
}

const cleanupEpgAutocompleteSearch = () => {
  searchPhrase.value = ''
  resetEpgAutocompleteSearchResults()
  if (showSearch.value) {
    toggleSearch()
  }
}

const submitSearch = () => {
  if (isEpgSearch.value) return
  const url = `/${searchType.value}?query=${encodeURIComponent(searchInput.value?.value.trim() ?? '')}`
  window.location.href = searchType.value === 'wer-streamt' ? `${url}#search-filters` : url
}

const performEpgAutocompleteSearch = async () => {
  searchInProgress.value = true
  hasApiError.value = false

  try {
    const axios = await getAxios()
    const url = rsConfig.value.epgAutocompleteSearchServiceUrl
    const params = { phrase: cleanUpSearchPhrase(searchPhrase.value) } // no schmutz in composer
    const { data } = await axios.get(url ?? '', { params }) || {}

    epgAutocompleteSearch.value = {
      channels: data.channels || [],
      broadcasts: data.broadcasts || {}
    }
  } catch (error) {
    resetEpgAutocompleteSearchResults()
    hasApiError.value = true
    console.error('Error fetching EPG autocomplete search results:', error)
  }
  searchInProgress.value = false
}

const triggerEpgAutocompleteSearch = () => {
  if (!searchPhrase.value) {
    resetEpgAutocompleteSearchResults()
    return
  }
  clearTimeout(debounceTimeoutId)
  debounceTimeoutId = setTimeout(performEpgAutocompleteSearch, 250) // be kind to the server
}

const selectSearchType = (type: SearchType) => {
  searchType.value = type
  setFocus()
  if (isEpgSearch.value && searchPhrase.value) {
    triggerEpgAutocompleteSearch()
  }
}

watch(epgAutocompleteSearch, keepTabbingFocusInHeaderSearch, { deep: true })
</script>

<template>
  <div class="bx-header__search bx-typo--header-search">
    <button ref="headerSearchOpener"
            aria-label="Suche öffnen"
            class="bx-header__search-toggle"
            @click="toggleSearch()"
            @keyup.enter="toggleSearch()">
      <Icon name="search" />
    </button>

    <form ref="headerSearchForm"
          class="bx-form bx-form--search-header bx-typo--form-search"
          onsubmit="return false;"
          @submit="submitSearch()">
      <input id="bx-header-search-input"
             ref="searchInput"
             v-model="searchPhrase"
             type="search"
             class="bx-form__input"
             :title="placeholder"
             :placeholder="placeholder"
             :autocomplete="isEpgSearch ? 'off' : 'on'"
             @keyup.enter.prevent="submitSearch()"
             @input="isEpgSearch && triggerEpgAutocompleteSearch()">
      <button aria-label="Suchen"
              class="bx-form__search-submit"
              tabindex="-1"
              @click="submitSearch()"
              @keyup.enter="submitSearch()">
        <Icon name="search" />
      </button>
      <div class="bx-form__search-tabs-wrapper">
        <div class="bx-form__search-tabs">
          <button v-for="type in searchTypes"
                  :key="type.value"
                  role="radio"
                  :aria-label="type.placeholder"
                  :aria-checked="searchType === type.value"
                  :data-search-type="type.value"
                  type="button"
                  :class="['bx-form__search-tab bx-typo__search-tab', { 'is-active': searchType === type.value }]"
                  @click.prevent="selectSearchType(type.value)">
            {{ type.label }}
          </button>
        </div>
      </div>

      <button aria-label="Suche schließen"
              type="button"
              tabindex="-1"
              class="bx-header__search-close"
              @click="toggleSearch()"
              @keyup.enter="toggleSearch()">
        <Icon name="close" />
      </button>

      <EpgAutocompleteFlyout v-if="isEpgSearch && searchPhrase"
                             :search-phrase="searchPhrase"
                             :epg-autocomplete-search="epgAutocompleteSearch"
                             :has-api-error="hasApiError"
                             :search-in-progress="searchInProgress"
                             @close-flyout="cleanupEpgAutocompleteSearch()" />
    </form>
  </div>
</template>
